import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, RadioGroup, FormControlLabel, Radio,
  FormControl, Checkbox, Alert
} from '@mui/material';
import MathMarkdown from './MathMarkdown';
import MathInput from './MathInput';
import { FollowupQuestion, FollowupChoice } from '../types';

interface FollowupDialogProps {
  open: boolean;
  followupQuestion: FollowupQuestion | null;
  followupChoices: FollowupChoice[];
  originalQuestionGraphUri?: string | null;
  originalQuestionGraphAlt?: string;
  onSubmit: (selectedChoices: string[], freeResponse: string) => Promise<void>;
  onError?: (error: string) => void;
}

export default function FollowupDialog({
  open,
  followupQuestion,
  followupChoices,
  originalQuestionGraphUri,
  originalQuestionGraphAlt,
  onSubmit,
  onError
}: FollowupDialogProps) {
  const [selectedFollowup, setSelectedFollowup] = useState<string[]>([]);
  const [followupFreeResponse, setFollowupFreeResponse] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedFollowup([]);
      setFollowupFreeResponse('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!followupQuestion) return;

    const followupType = followupQuestion.followup_type;

    // Validation based on type
    if (followupType === 'MC' && selectedFollowup.length !== 1) {
      const errorMsg = 'Please select one option';
      setError(errorMsg);
      onError?.(errorMsg);
      return;
    }
    if (followupType === 'AA' && selectedFollowup.length === 0) {
      const errorMsg = 'Please select at least one option';
      setError(errorMsg);
      onError?.(errorMsg);
      return;
    }
    if (followupType === 'FR' && !followupFreeResponse.trim()) {
      const errorMsg = 'Please enter your answer';
      setError(errorMsg);
      onError?.(errorMsg);
      return;
    }

    setSubmitting(true);
    setError('');
    
    try {
      await onSubmit(selectedFollowup, followupFreeResponse);
    } catch {
      const errorMsg = 'Failed to submit response. Please try again.';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!followupQuestion) return null;

  const graphUri = originalQuestionGraphUri?.startsWith('/api')
    ? `${import.meta.env.VITE_API_URL?.replace(/\/api$/, '')}${originalQuestionGraphUri}`
    : originalQuestionGraphUri;

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      aria-labelledby="followup-dialog-title"
    >
      <DialogTitle id="followup-dialog-title">
        Follow-up Question
      </DialogTitle>
      <DialogContent>
        <FormControl component="fieldset" fullWidth>
          {graphUri && graphUri.trim() !== '' && (
            <Box
              sx={{
                mb: 2,
                textAlign: 'center',
                border: '1px solid #ccc',
                borderRadius: 1,
                p: 1,
                backgroundColor: '#fafafa',
              }}
            >
              <img
                src={graphUri}
                alt={originalQuestionGraphAlt || 'Graph from the original question'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '260px',
                  objectFit: 'contain',
                  borderRadius: '4px',
                }}
              />
            </Box>
          )}

          <MathMarkdown sx={{ mb: 2, '& p': { mt: 0, mb: 1 } }}>
            {followupQuestion.followup_text}
          </MathMarkdown>
          
          {/* Multiple Choice (MC) - Radio buttons */}
          {followupQuestion.followup_type === 'MC' && (
            <RadioGroup
              value={selectedFollowup[0] || ''}
              onChange={(e) => setSelectedFollowup([e.target.value])}
            >
              {followupChoices.map((choice) => (
                <FormControlLabel
                  key={choice.choice_id}
                  value={choice.choice_id}
                  control={<Radio />}
                  label={
                    <MathMarkdown sx={{ display: 'inline-block', verticalAlign: 'middle', '& p': { display: 'inline', m: 0 } }}>
                      {`${choice.choice_letter}. ${choice.choice_text}`}
                    </MathMarkdown>
                  }
                  sx={{ mb: 1, alignItems: 'center' }}
                />
              ))}
            </RadioGroup>
          )}
          
          {/* All that Apply (AA) - Checkboxes */}
          {followupQuestion.followup_type === 'AA' && (
            <Box>
              {followupChoices.map((choice) => (
                <FormControlLabel
                  key={choice.choice_id}
                  control={
                    <Checkbox
                      checked={selectedFollowup.includes(choice.choice_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFollowup([...selectedFollowup, choice.choice_id]);
                        } else {
                          setSelectedFollowup(selectedFollowup.filter(id => id !== choice.choice_id));
                        }
                      }}
                    />
                  }
                  label={
                    <MathMarkdown sx={{ display: 'inline-block', verticalAlign: 'middle', '& p': { display: 'inline', m: 0 } }}>
                      {`${choice.choice_letter}. ${choice.choice_text}`}
                    </MathMarkdown>
                  }
                  sx={{ display: 'flex', mb: 1, alignItems: 'center' }}
                />
              ))}
            </Box>
          )}
          
          {/* Free Response (FR) - Text input */}
          {followupQuestion.followup_type === 'FR' && (
            <MathInput
              value={followupFreeResponse}
              onChange={(value) => setFollowupFreeResponse(value)}
              disabled={submitting}
              label="Your Answer"
            />
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} role="alert">
              {error}
            </Alert>
          )}
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            submitting ||
            (followupQuestion.followup_type === 'MC' ? selectedFollowup.length === 0 :
            followupQuestion.followup_type === 'AA' ? selectedFollowup.length === 0 :
            followupQuestion.followup_type === 'FR' ? !followupFreeResponse.trim() :
            true)
          }
        >
          {submitting ? 'Submitting...' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
